import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Menu, X, User, LogOut, ShoppingBag, ShoppingCart, FileText, Settings, KeyRound, ChevronDown, Briefcase, ClipboardCheck, Info, Star, HelpCircle, TrendingUp } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useCart } from "@/contexts/CartContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { Badge } from "@/components/ui/badge";
import { useSiteAssetUrl } from "@/contexts/SiteAssetsContext";
// Local fallback for resilience
import logoFallback from "@/assets/logo.png";
import { cn } from "@/lib/utils";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { user, signOut, isAdmin, isAffiliate } = useAuth();
  const { itemCount } = useCart();
  const location = useLocation();
  // Get logo from site assets (with local fallback)
  const logoUrl = useSiteAssetUrl('logo');
  const logo = logoUrl || logoFallback;
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    setIsMenuOpen(false);
  };

  const scrollToSection = (sectionId: string) => {
    setIsMenuOpen(false);
    
    const scrollToElement = () => {
      const element = document.getElementById(sectionId);
      if (element) {
        const headerOffset = 80;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.scrollY - headerOffset;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    };
    
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(scrollToElement, 150);
    } else {
      scrollToElement();
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-card/80 backdrop-blur-lg border-b border-border/50">
      <div className="container-custom">
        <nav className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img 
              src={logo} 
              alt="MyCreditScrub Logo" 
              width={169}
              height={56}
              fetchPriority="high"
              decoding="sync"
              style={{ aspectRatio: '169/56' }}
              className="h-12 lg:h-14 w-auto" 
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center">
            <NavigationMenu>
              <NavigationMenuList className="gap-1">
                {/* Templates - Direct Link */}
                <NavigationMenuItem>
                  <NavigationMenuLink asChild>
                    <Link 
                      to="/templates"
                      className={cn(
                        "group inline-flex h-10 w-max items-center justify-center rounded-md bg-transparent px-4 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none disabled:pointer-events-none disabled:opacity-50",
                        "text-muted-foreground hover:text-foreground"
                      )}
                    >
                      <FileText className="w-4 h-4 mr-1.5" />
                      Templates
                    </Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>

                {/* Services Dropdown */}
                <NavigationMenuItem>
                  <NavigationMenuTrigger className="bg-transparent text-muted-foreground hover:text-foreground hover:bg-accent data-[state=open]:bg-accent">
                    <Briefcase className="w-4 h-4 mr-1.5" />
                    Services
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[280px] gap-1 p-2 bg-popover">
                      <li>
                        <Link to="/packet-review" className="block select-none rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                          <div className="flex items-center gap-2 text-sm font-medium leading-none">
                            <ClipboardCheck className="w-4 h-4 text-primary" />
                            Packet Review
                          </div>
                          <p className="mt-1.5 text-xs leading-snug text-muted-foreground">
                            Expert review of your dispute packet
                          </p>
                        </Link>
                      </li>
                      <li>
                        <Link to="/done-for-you" className="block select-none rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                          <div className="flex items-center gap-2 text-sm font-medium leading-none">
                            <Briefcase className="w-4 h-4 text-primary" />
                            Done-For-You
                          </div>
                          <p className="mt-1.5 text-xs leading-snug text-muted-foreground">
                            Full-service credit dispute assistance
                          </p>
                        </Link>
                      </li>
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                {/* About Dropdown */}
                <NavigationMenuItem>
                  <NavigationMenuTrigger className="bg-transparent text-muted-foreground hover:text-foreground hover:bg-accent data-[state=open]:bg-accent">
                    <Info className="w-4 h-4 mr-1.5" />
                    About
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[220px] gap-1 p-2 bg-popover">
                      <li>
                        <button
                          onClick={() => scrollToSection("how-it-works")}
                          className="w-full text-left block select-none rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                        >
                          <div className="flex items-center gap-2 text-sm font-medium leading-none">
                            <Info className="w-4 h-4 text-primary" />
                            How It Works
                          </div>
                        </button>
                      </li>
                      <li>
                        <button
                          onClick={() => scrollToSection("reviews")}
                          className="w-full text-left block select-none rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                        >
                          <div className="flex items-center gap-2 text-sm font-medium leading-none">
                            <Star className="w-4 h-4 text-primary" />
                            Reviews
                          </div>
                        </button>
                      </li>
                      <li>
                        <button
                          onClick={() => scrollToSection("faq")}
                          className="w-full text-left block select-none rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                        >
                          <div className="flex items-center gap-2 text-sm font-medium leading-none">
                            <HelpCircle className="w-4 h-4 text-primary" />
                            FAQ
                          </div>
                        </button>
                      </li>
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
          </div>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-3">
            <Link 
              to="/cart" 
              className="relative inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 hover:bg-accent hover:text-accent-foreground h-10 w-10"
              aria-label={itemCount > 0 ? `View shopping cart with ${itemCount} items` : "View shopping cart"}
            >
              <ShoppingCart className="w-5 h-5" />
              {itemCount > 0 && (
                <Badge className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center text-xs bg-primary">
                  {itemCount}
                </Badge>
              )}
            </Link>
            
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-2">
                    <User className="w-4 h-4" />
                    Account
                    <ChevronDown className="w-3 h-3 opacity-50" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-popover">
                  <div className="px-2 py-1.5 text-sm text-muted-foreground truncate">
                    {user.email}
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/my-purchases" className="flex items-center gap-2 cursor-pointer">
                      <ShoppingBag className="w-4 h-4" />
                      My Purchases
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/account" className="flex items-center gap-2 cursor-pointer">
                      <Settings className="w-4 h-4" />
                      Account Settings
                    </Link>
                  </DropdownMenuItem>
                  {isAffiliate && (
                    <DropdownMenuItem asChild>
                      <Link to="/affiliate" className="flex items-center gap-2 cursor-pointer">
                        <TrendingUp className="w-4 h-4" />
                        Affiliate Dashboard
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {isAdmin && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin" className="flex items-center gap-2 cursor-pointer">
                        <User className="w-4 h-4" />
                        Admin Panel
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSignOut} className="flex items-center gap-2 cursor-pointer text-destructive">
                    <LogOut className="w-4 h-4" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="gap-1.5">
                      Sign In
                      <ChevronDown className="w-3 h-3 opacity-50" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48 bg-popover">
                    <DropdownMenuItem asChild>
                      <Link to="/auth" className="flex items-center gap-2 cursor-pointer">
                        <User className="w-4 h-4" />
                        Sign In / Sign Up
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/request-access" className="flex items-center gap-2 cursor-pointer">
                        <KeyRound className="w-4 h-4" />
                        Access Purchases
                      </Link>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Link to="/templates">
                  <Button variant="gold" size="sm">
                    Get Started
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-foreground"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </nav>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden py-4 border-t border-border/50 animate-fade-in">
            <div className="flex flex-col gap-4">
              <Link to="/templates" onClick={() => setIsMenuOpen(false)} className="text-muted-foreground hover:text-foreground transition-colors font-medium py-2 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Templates
              </Link>
              <Link to="/cart" onClick={() => setIsMenuOpen(false)} className="text-muted-foreground hover:text-foreground transition-colors font-medium py-2 flex items-center gap-2">
                <ShoppingCart className="w-4 h-4" />
                Cart {itemCount > 0 && `(${itemCount})`}
              </Link>
              
              {/* Services Section */}
              <div className="border-t border-border/30 pt-3">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Services</p>
                <Link to="/packet-review" onClick={() => setIsMenuOpen(false)} className="text-muted-foreground hover:text-foreground transition-colors font-medium py-2 flex items-center gap-2">
                  <ClipboardCheck className="w-4 h-4" />
                  Packet Review
                </Link>
                <Link to="/done-for-you" onClick={() => setIsMenuOpen(false)} className="text-muted-foreground hover:text-foreground transition-colors font-medium py-2 flex items-center gap-2">
                  <Briefcase className="w-4 h-4" />
                  Done-For-You
                </Link>
              </div>
              
              {/* About Section */}
              <div className="border-t border-border/30 pt-3">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">About</p>
                <button 
                  onClick={() => scrollToSection("how-it-works")} 
                  className="text-muted-foreground hover:text-foreground transition-colors font-medium py-2 text-left flex items-center gap-2 w-full"
                >
                  <Info className="w-4 h-4" />
                  How It Works
                </button>
                <button 
                  onClick={() => scrollToSection("reviews")} 
                  className="text-muted-foreground hover:text-foreground transition-colors font-medium py-2 text-left flex items-center gap-2 w-full"
                >
                  <Star className="w-4 h-4" />
                  Reviews
                </button>
                <button 
                  onClick={() => scrollToSection("faq")} 
                  className="text-muted-foreground hover:text-foreground transition-colors font-medium py-2 text-left flex items-center gap-2 w-full"
                >
                  <HelpCircle className="w-4 h-4" />
                  FAQ
                </button>
              </div>
              
              {user ? (
                <>
                  <div className="border-t border-border/50 pt-4 mt-2">
                    <p className="text-sm text-muted-foreground mb-3">{user.email}</p>
                    <div className="flex flex-col gap-3">
                      <Link to="/my-purchases" onClick={() => setIsMenuOpen(false)}>
                        <Button variant="outline" className="w-full gap-2">
                          <ShoppingBag className="w-4 h-4" />
                          My Purchases
                        </Button>
                      </Link>
                      <Link to="/account" onClick={() => setIsMenuOpen(false)}>
                        <Button variant="outline" className="w-full gap-2">
                          <Settings className="w-4 h-4" />
                          Account Settings
                        </Button>
                      </Link>
                      {isAffiliate && (
                        <Link to="/affiliate" onClick={() => setIsMenuOpen(false)}>
                          <Button variant="outline" className="w-full gap-2">
                            <TrendingUp className="w-4 h-4" />
                            Affiliate Dashboard
                          </Button>
                        </Link>
                      )}
                      {isAdmin && (
                        <Link to="/admin" onClick={() => setIsMenuOpen(false)}>
                          <Button variant="outline" className="w-full gap-2">
                            <User className="w-4 h-4" />
                            Admin Panel
                          </Button>
                        </Link>
                      )}
                      <Button variant="destructive" className="w-full gap-2" onClick={handleSignOut}>
                        <LogOut className="w-4 h-4" />
                        Sign Out
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex flex-col gap-3 pt-4 border-t border-border/50">
                  <Link to="/auth" onClick={() => setIsMenuOpen(false)}>
                    <Button variant="outline" className="w-full">
                      Sign In
                    </Button>
                  </Link>
                  <Link to="/request-access" onClick={() => setIsMenuOpen(false)}>
                    <Button variant="ghost" className="w-full gap-2">
                      <KeyRound className="w-4 h-4" />
                      Access Purchases
                    </Button>
                  </Link>
                  <Link to="/templates" onClick={() => setIsMenuOpen(false)}>
                    <Button variant="gold" className="w-full">
                      Get Started
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
